import { Component, OnInit, Inject, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { AuthService } from 'src/app/auth/service/auth.service';
import { NotificationService } from '../notification-service/notification.service';
import { ShareThisViewService } from './share-this-view.service';
import { catchError, finalize, map, share, tap } from 'rxjs/operators';
import {
  AccessLink,
  AccessLinksService,
  CreateShareInvitationRequest,
  DefaultService,
  InvitationsService,
  ShareAccessLinkRequest,
  UserRole,
  UserRolesService,
} from 'ldt-identity-service-api';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Log } from 'src/app/logger.service';

export interface ShareDialogData {
  orgId: string;
  redirectUrl: string;
  config: any;
}

// IDs for existing users, if not its a new user
export interface SelectedShareUser {
  id?: string;
  email: string;
  name?: string;
}

@Component({
    selector: 'app-share-this-view-dialog',
    templateUrl: './share-this-view-dialog.component.html',
    styleUrls: ['./share-this-view-dialog.component.scss'],
    standalone: false
})
export class ShareThisViewDialogComponent implements OnInit, OnDestroy {
  @ViewChild('emailInputField') emailInputField!: ElementRef<HTMLInputElement>;

  emailInput = new FormControl('', [
    Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
  ]);

  view: 'main' | 'invite' = 'main';

  // Public link properties
  publicLink: AccessLink | null = null;
  isGeneratingPublicLink: boolean = false;

  // Private link properties
  privateLink: AccessLink | null = null;
  isGeneratingPrivateLink: boolean = false;

  // Invitation properties
  isSharing: boolean = false;

  // User list properties
  orgUsers: UserRole[] = [];
  selectedUsers: SelectedShareUser[] = [];
  filteredOrgUsers: UserRole[] = [];
  isLoadingUsers: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<ShareThisViewDialogComponent>,
    private clipboard: Clipboard,
    private notificationService: NotificationService,
    private shareService: ShareThisViewService,
    protected authService: AuthService,
    private userRolesService: UserRolesService,
    private invitationsService: InvitationsService,
    private idShareService: AccessLinksService,
    @Inject(MAT_DIALOG_DATA) public data: ShareDialogData
  ) {}

  ngOnInit(): void {
    // Subscribe to input changes to filter users with debounce
    this.emailInput.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.filterUsers(value || '');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showInviteView(): void {
    this.view = 'invite';
    this.loadOrgUsers();
    // ensure the input is visible before focusing
    setTimeout(() => {
      this.emailInputField?.nativeElement.focus();
    });
  }

  cancelUserSelection(): void {
    this.emailInput.reset();
    this.selectedUsers = [];
  }

  loadOrgUsers(): void {
    if (this.orgUsers.length > 0) {
      return;
    }

    this.isLoadingUsers = true;
    this.userRolesService.getOrgUsers(this.data.orgId).subscribe({
      next: (users) => {
        const currentUserEmail = this.authService.getUserValue?.email;
        if (currentUserEmail) {
          this.orgUsers = users.filter((user) => user.user?.email !== currentUserEmail);
        } else {
          this.orgUsers = users;
        }

        // Initialize filtered users
        this.filterUsers(this.emailInput.value || '');
        this.isLoadingUsers = false;
      },
      error: (err) => {
        Log.s(
          'ShareThisViewDialogComponent loadOrgUsers() failed to load organization users',
          'error',
          {
            error: err.message,
          }
        );
        this.isLoadingUsers = false;
        this.notificationService.error('Failed to load organization users');
      },
    });
  }

  filterUsers(filterValue: string): void {
    filterValue = filterValue.toLowerCase().trim();

    // if no filter, show all users sorted by name
    if (!filterValue) {
      this.filteredOrgUsers = [...this.orgUsers].sort((a, b) =>
        this.getUserName(a).localeCompare(this.getUserName(b))
      );
      return;
    }

    // sort the users by email and name
    this.filteredOrgUsers = this.orgUsers
      .filter((user) => {
        const name = this.getUserName(user).toLowerCase();
        const email = this.getUserEmail(user).toLowerCase();

        return name.includes(filterValue) || email.includes(filterValue);
      })
      .sort((a, b) => this.getUserName(a).localeCompare(this.getUserName(b)));
  }

  toggleUserSelection(user: UserRole): void {
    const index = this.selectedUsers.findIndex(
      (selected) => selected.email === this.getUserEmail(user)
    );
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push({ id: user.user?.id, email: user.user?.email || '' });
    }
    this.emailInput.setValue('');

    // Keep the input focused to allow for more selections
    this.emailInputField.nativeElement.focus();
  }

  removeSelectedUser(user: SelectedShareUser): void {
    const index = this.selectedUsers.findIndex((selected) => selected.email === user.email);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    }
  }

  isSelected(user: UserRole): boolean {
    return this.selectedUsers.some((selected) => selected.id === user.user?.id);
  }

  addNewEmail(): void {
    if (!this.emailInput.value || !this.isValidEmail(this.emailInput.value)) {
      return;
    }
    const email = this.emailInput.value.trim();
    if (this.selectedUsers.some((u) => u.email === email)) {
      this.emailInput.reset();
      return;
    }

    this.selectedUsers.push({ email: email });
    this.emailInput.reset();
  }

  // support for 'enter' key under certain conditions (ergonomics)
  onEnterKey(event: Event): void {
    // first, check if we can add a new user's email to the box
    const isInviteFlow =
      !this.isLoadingUsers &&
      this.filteredOrgUsers.length === 0 &&
      this.isValidEmail(this.emailInput.value || '');

    if (isInviteFlow) {
      event.preventDefault();
      this.addNewEmail();
      return;
    }

    // next, check if 'enter' can trigger the share flow
    const canShare = this.selectedUsers.length > 0 && !this.isSharing;
    const isInputEmpty = !this.emailInput.value?.trim();

    if (canShare && isInputEmpty) {
      event.preventDefault();
      this.share();
    }
  }

  share(): void {
    if (this.selectedUsers.length === 0 || this.isSharing) {
      return;
    }

    this.isSharing = true;
    this.generatePrivateLink().subscribe({
      next: (link) => {
        if (!link) {
          this.notificationService.error('Sorry, something went wrong. Please try again later.');
          Log.s('ShareThisViewDialogComponent share() failed to generate private link', 'error', {
            orgId: this.data.orgId,
          });
          this.isSharing = false;
          return;
        }

        const newUsers = this.selectedUsers.filter((u) => !u.id);
        const existingUsers = this.selectedUsers.filter((u) => u.id);

        newUsers.forEach((user) => {
          this.inviteUser(user.email, link).subscribe((success) => {
            if (success) {
              this.selectedUsers = this.selectedUsers.filter((u) => u.email !== user.email);
            }
          });
        });

        if (existingUsers.length > 0) {
          const req: ShareAccessLinkRequest = {
            to: existingUsers.map((u) => u.email),
          };

          this.idShareService.shareAccessLink(this.data.orgId, link.id.toString(), req).subscribe({
            next: () => {
              this.notificationService.info(`Shared with ${existingUsers.length} existing users.`);
            },
            error: (err) => {
              Log.s(
                'ShareThisViewDialogComponent share() failed to share with existing users',
                'error',
                {
                  orgId: this.data.orgId,
                  accessLinkId: link.id.toString(),
                  error: err,
                }
              );
              this.notificationService.error(
                `Failed to share with ${existingUsers.length} existing users.`
              );
            },
          });
        }

        this.dialogRef.close();
      },
      error: (err) => {
        this.isSharing = false;
        Log.s('ShareThisViewDialogComponent share() failed to generate private link', 'error', {
          error: err,
          orgId: this.data.orgId,
        });
        this.notificationService.error('Sorry, something went wrong. Please try again later.');
      },
    });
  }

  private inviteUser(email: string, link: AccessLink): Observable<boolean> {
    const inviteReq: CreateShareInvitationRequest = {
      invitee_email: email,
      invited_by_name: this.authService.getUserValue.name,
      role: CreateShareInvitationRequest.RoleEnum.Admin,
      redirect_url: `/go/${link.token}`,
      settings: this.data.config,
    };

    // Return the invitation observable directly
    return this.invitationsService
      .createShareInvitation('workforce', this.data.orgId, inviteReq)
      .pipe(
        map(() => {
          this.notificationService.info(`${email} successfully invited.`);
          return true;
        }),
        catchError((err) => {
          if (err.status && err.status === 409) {
            this.notificationService.error('User already has access to this organization.');
          } else {
            Log.s('ShareThisViewDialogComponent inviteUser() failed to invite user', 'error', {
              email: email,
              error: err.message,
            });
            this.notificationService.error(`Failed to invite ${email}. Please try again later.`);
          }
          return of(false);
        })
      );
  }

  private generatePublicLink(): Observable<AccessLink | null> {
    if (this.publicLink) return of(this.publicLink);
    this.isGeneratingPublicLink = true;

    return this.shareService
      .createPublicLink(this.data.orgId, this.data.redirectUrl, this.data.config)
      .pipe(
        share(),
        tap((link) => {
          this.publicLink = link;
        }),
        finalize(() => {
          this.isGeneratingPublicLink = false;
        })
      );
  }

  private generatePrivateLink(): Observable<AccessLink | null> {
    if (this.privateLink) return of(this.privateLink);
    this.isGeneratingPrivateLink = true;

    return this.shareService
      .createPrivateLink(this.data.orgId, this.data.redirectUrl, this.data.config)
      .pipe(
        tap((link) => {
          this.privateLink = link;
        }),
        finalize(() => {
          this.isGeneratingPrivateLink = false;
        })
      );
  }

  getUrlForAccessLink(link: AccessLink): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/go/${link.token}`;
  }

  copyPublicLink(): void {
    this.generatePublicLink().subscribe((link) => {
      if (!link) return;

      this.clipboard.copy(this.getUrlForAccessLink(link));
      this.notificationService.info('Public link copied to clipboard');
    });
  }

  copyPrivateLink(): void {
    this.generatePrivateLink().subscribe((link) => {
      if (!link) return;

      this.clipboard.copy(this.getUrlForAccessLink(link));
      this.notificationService.info('Private link copied to clipboard');
    });
  }

  getAvatarColor(name: string): string {
    // Generate a consistent color based on the user's name
    const colors = [
      '#F44336',
      '#E91E63',
      '#9C27B0',
      '#673AB7',
      '#3F51B5',
      '#2196F3',
      '#03A9F4',
      '#00BCD4',
      '#009688',
      '#4CAF50',
      '#8BC34A',
      '#CDDC39',
      '#FFC107',
      '#FF9800',
      '#FF5722',
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }

  getUserInitials(user: UserRole): string {
    return this.getInitials(user.user?.name);
  }

  getSelectedUserInitials(user: SelectedShareUser): string {
    if (user.id) {
      const userName = this.orgUsers.find((u) => u.user?.id === user.id)?.user?.name;
      return this.getInitials(userName);
    }
    return user.email;
  }

  private getInitials(name: string | null | undefined): string {
    if (!name) {
      return '';
    }
    return name
      .split(' ')
      .filter((n) => n)
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  getUserName(user: UserRole): string {
    return user.user?.name || 'Unknown User';
  }

  getSelectedUserName(user: SelectedShareUser): string {
    return user.id
      ? this.orgUsers.find((u) => u.user?.id === user.id)?.user?.name || user.email
      : user.email;
  }

  getUserEmail(user: UserRole): string {
    return user.user?.email || '';
  }

  getUserRole(user: UserRole): string {
    return user.role || '';
  }

  trackByUser(index: number, item: UserRole): string {
    return item.user?.id || index.toString();
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }
}
